
import React, { useEffect, useState, useRef } from 'react';
import { Box, TableBody, Typography, CircularProgress, TableContainer, Table } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  UploadArea,
  UploadButton,
  SummaryCard,
  SummaryItem,
  SummaryValue,
  SummaryLabel,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StatusChip
} from './styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import {
  bulkUploadWorkers,
  setBulkUploadData,
  updateBulkUploadProgress,
  resetBulkUploadState,
} from '../../../modules/worker/actions';
import {
  selectWorkerBulkUploadData,
  selectWorkerBulkUploadLoading,
  selectWorkerBulkUploadError
} from '../../../modules/worker/selectors';
import AlertPopup from '../../../components/common/AlertPopup';
import CustomPagination from '../../../components/CustomPagination';
import { API_URL } from '../../../utils/env';

const WorkerBulkUpload: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadData = useSelector(selectWorkerBulkUploadData);
  const loading = useSelector(selectWorkerBulkUploadLoading);
  const error = useSelector(selectWorkerBulkUploadError);
  const { accessToken } = useSelector((state: any) => state.login);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(1);
  const ws = useRef<WebSocket | null>(null);
  const PAGE_SIZE = 10;

  useEffect(() => {
    setActivePage('worker'); // Highlight worker in sidebar
    setPath([
      { title: 'Management', path: '/workers' },
      { title: 'Worker', path: '/workers' },
      { title: 'Bulk Upload', path: '/worker/bulk-upload' },
    ]);
    setIsShow(true);

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [setActivePage, setPath, setIsShow]);

  const connectWebSocket = () => {
    if (!accessToken) {
      console.error('No access token available for WebSocket');
      setAlert({ message: 'Authentication required. Please login again.', severity: 'error' });
      return;
    }

    if (ws.current) ws.current.close();

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let host = '';
    try {
      const url = new URL(API_URL);
      host = url.host;
    } catch (e) {
      host = API_URL.replace(/^https?:\/\//, '').split('/')[0];
    }
    const wsUrl = `${protocol}//${host}/ws/worker/bulk-upload/?token=${accessToken}`;
    console.log('🔗 Attempting WebSocket connection to:', wsUrl.split('?')[0]);

    try {
      const socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('✅ Bulk upload websocket connected');
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📥 WS Message:', data.type);
          if (data.type === 'row_update') {
            dispatch(updateBulkUploadProgress({ detail: data.detail, summary: data.summary }));
          } else if (data.type === 'summary') {
            dispatch(setBulkUploadData({
              message: 'Process started...',
              summary: {
                total: data.total,
                success: data.success,
                warning: data.warning,
                failed: data.failed
              },
              details: []
            }));
          } else if (data.type === 'completed') {
            setAlert({ message: data.message, severity: 'success' });
          }
        } catch (err) {
          console.error('❌ WS Data Parse Error:', err, event.data);
        }
      };

      socket.onerror = (error) => {
        console.error('❌ WebSocket Error Event:', error);
        // On error, the browser doesn't give much detail for security reasons
        // but it often means a 403, 404, or 502 at the proxy level.
      };

      socket.onclose = (event) => {
        console.warn(`🔌 WS Disconnected | Code: ${event.code} | Reason: ${event.reason || 'None'} | Clean: ${event.wasClean}`);
        if (event.code === 1006) {
          console.error('🚨 Error 1006 usually means the Proxy (Nginx) is not configured to allow WebSockets or the server dropped the connection before it reached Django.');
        }
      };

      ws.current = socket;
    } catch (e) {
      console.error('❌ Failed to create WebSocket instance:', e);
    }
  };


  useEffect(() => {
    if (error) {
      setAlert({ message: error, severity: 'error' });
    }
  }, [error]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setAlert({ message: 'Please select a valid CSV file.', severity: 'error' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadClick = async () => {
    if (selectedFile) {
      dispatch(resetBulkUploadState());
      connectWebSocket();
      // Give the websocket a moment to establish connection before starting the upload
      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await dispatch(bulkUploadWorkers(selectedFile));
      if (bulkUploadWorkers.fulfilled.match(result)) {
        setAlert({ message: result.payload.message, severity: 'success' });
        setSelectedFile(null);
      }
    }
  };

  const handleDownloadSample = () => {
    const headers = [
      "email", "first_name", "last_name", "phone_number", "gender", "date_of_birth",
      "emergency_contact", "years_of_experience", "location_name", "address",
      "latitude", "longitude", "maximum_available_days_per_week",
      "preferred_roles", "skills", "qualifications"
    ];

    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "worker_bulk_upload_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const paginatedDetails = uploadData?.details.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) || [];
  const totalPages = uploadData ? Math.ceil(uploadData.details.length / PAGE_SIZE) : 0;

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}

      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Worker Bulk Upload</PageTitle>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <UploadButton
              startIcon={<FileDownloadIcon />}
              sx={{ backgroundColor: '#f0f0f0', color: '#333', '&:hover': { backgroundColor: '#e0e0e0' } }}
              onClick={handleDownloadSample}
            >
              Download Sample CSV
            </UploadButton>
          </Box>
        </HeaderContainer>

        <UploadArea onClick={() => fileInputRef.current?.click()}>
          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".csv"
          />
          <CloudUploadIcon sx={{ fontSize: 64, color: '#5E8E7E' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedFile ? selectedFile.name : 'Click to select or drag and drop CSV file'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Large files may take a few moments to process.
            </Typography>
          </Box>
          <UploadButton
            variant="contained"
            disabled={!selectedFile || loading}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleUploadClick();
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Upload and Process'}
          </UploadButton>
        </UploadArea>

        {uploadData && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Processing Summary</Typography>
            <SummaryCard elevation={0}>
              <SummaryItem>
                <SummaryValue>{uploadData.summary.total}</SummaryValue>
                <SummaryLabel>Total Rows</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue color="#2E7D32">{uploadData.summary.success}</SummaryValue>
                <SummaryLabel>Success</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue color="#F9A825">{uploadData.summary.warning}</SummaryValue>
                <SummaryLabel>Warnings</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue color="#C62828">{uploadData.summary.failed}</SummaryValue>
                <SummaryLabel>Failed</SummaryLabel>
              </SummaryItem>
            </SummaryCard>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Detailed Results</Typography>
            <TableContainer sx={{ borderRadius: '8px', border: '1px solid #E0E0E0' }}>
              <Table>
                <StyledTableHead>
                  <StyledTableRow>
                    <StyledTableCell>Row #</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Message</StyledTableCell>
                  </StyledTableRow>
                </StyledTableHead>
                <TableBody>
                  {paginatedDetails.map((detail, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{detail.row}</StyledTableCell>
                      <StyledTableCell>{detail.email}</StyledTableCell>
                      <StyledTableCell>
                        <StatusChip label={detail.status.toUpperCase()} status={detail.status} />
                      </StyledTableCell>
                      <StyledTableCell>{detail.message}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <CustomPagination
                  totalPages={totalPages}
                  currentPage={page}
                  onPageChange={handlePageChange}
                />
              </Box>
            )}
          </Box>
        )}
      </MainContainer>
    </Box>
  );
};

export default WorkerBulkUpload;
